import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useRecoilValue } from "recoil";
import { Skeleton } from "@mui/material";
import {
  Box,
  Avatar,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Paper
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { firebaseState, themeOSState } from "../../store/AuthAtoms";
const CONVERSATIONS = "consultantConversations";

/* i had to rerun the script because a condition broke the it,  */
const ConsultantChat = ({ diagramId, inputMessage, setInputMessage }) => {
  const firebase = useRecoilValue(firebaseState);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!firebase || !diagramId.trim()) return;
    const diagramsQuery = firebase.db.collection(CONVERSATIONS).where("diagramId", "==", diagramId);
    const unsubscribeDiagrams = diagramsQuery.onSnapshot(snapshot => {
      const changes = snapshot.docChanges();

      if (changes.length > 0) {
        const changeData = changes[0].doc.data();
        setMessages(changeData.conversation);
      }
    });
    return () => {
      unsubscribeDiagrams();
    };
  }, [firebase, diagramId]);

  const handleSendMessage = async e => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    const messageId = firebase.db.collection("some-collection").doc().id;
    const userMessage = {
      id: messageId,
      sender: "user",
      text: inputMessage,
      timestamp: new Date(),
      isConsultant: false
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      await axios.post("/consultantapi", {
        prompt: inputMessage,
        messageId,
        diagramId
      });
    } catch (error) {
      // console.error("Error sending message:", error);
      /*       const errorMessage = {
        id: Date.now() + 1,
        sender: "consultant",
        text: "Sorry, I'm having trouble responding right now.",
        timestamp: new Date(),
        isConsultant: true,
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]); */
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "90vh",
        width: "100%",
        overflow: "hidden",
        border: "1px solid gray",
        borderRadius: "25px"
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          bgcolor: "background.default"
        }}
      >
        {messages.length === 0 && !isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "text.secondary",
              mt: "200px"
            }}
          >
            <Typography>Start your conversation </Typography>
          </Box>
        ) : (
          <List
            sx={{
              "&::-webkit-scrollbar": {
                display: "none"
              }
            }}
          >
            {messages.map(message => (
              <React.Fragment key={message.id}>
                <ListItem
                  sx={{
                    justifyContent: message.isConsultant ? "flex-start" : "flex-end",
                    alignItems: "flex-start"
                  }}
                >
                  {message.isConsultant && (
                    <ListItemAvatar>
                      <Avatar src={message.sender.avatar} alt={message.sender.name} />
                    </ListItemAvatar>
                  )}
                  <Paper
                    elevation={2}
                    sx={{
                      p: "7px",
                      maxWidth: "70%",
                      bgcolor: theme => (theme.palette.mode === "dark" ? "#2d2b2b" : "#cdcdcd"),
                      color: message.isError ? "red" : theme => (theme.palette.mode === "dark" ? "white" : "black"),
                      borderRadius: message.isConsultant ? "4px 20px 20px 20px" : "20px 4px 20px 20px"
                    }}
                  >
                    <ListItemText
                      primary={message.text}
                      secondaryTypographyProps={{
                        color: message.isError ? "red" : "white",
                        variant: "caption"
                      }}
                    />
                  </Paper>
                  {!message.isConsultant && (
                    <ListItemAvatar sx={{ ml: 1 }}>
                      <Avatar src={message.sender.avatar} alt={message.sender.name} />
                    </ListItemAvatar>
                  )}
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", ml: "10px" }}>
            <Avatar src={""} alt={"consultant.name"} sx={{ mr: 1 }} />
            <Skeleton variant="rectangular" width="80%" height={100} sx={{ borderRadius: "4px 20px 20px 20px" }} />
          </Box>
        )}
      </Box>

      <Box
        component="form"
        sx={{
          display: "flex",
          alignItems: "flex-end",
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          position: "sticky",
          bottom: 0,
          py: 3,
          px: 1
        }}
      >
        {/* */}
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={inputMessage}
          onChange={e => setInputMessage(e.target.value)}
          placeholder="Type your message here..."
          disabled={isLoading}
          sx={{ mr: 1, flexGrow: 1 }}
          multiline
          minRows={3}
          onKeyDown={event => {
            console.log(event, "event");
            if (event.key === "Enter" && !event.shiftKey) {
              console.log("Enter key pressed");
              handleSendMessage(event);
            }
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center"
          }}
        >
          <SendIcon
            onClick={handleSendMessage}
            sx={{
              backgroundColor: "#72cb72",
              borderRadius: "50%",
              width: 25,
              height: 25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 1,
              color: "white",
              cursor: "pointer",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                backgroundColor: "gray",
                transform: "scale(1.1)"
              },
              "&:active": {
                backgroundColor: "darkgray",
                transform: "scale(1.05)"
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ConsultantChat;
